import { Divider } from '@heroui/react';
import React from 'react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';

import { ChocoCheckbox } from '../../../../../components/FormComponents';
import { useSsoRestriction } from '../../../contexts';
import { SsoRestrictionTooltip } from '../../SsoRestrictionTooltip';
import { SupportLogin } from '../../SupportLogin';
import { UserFormData } from '../hooks/useUserFormValidation';

interface UserTypeSectionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  isEditMode?: boolean;
  disabled?: boolean;
  isFederatedUser?: boolean;
  /** Buyers the user is linked to now (or selected, when creating). */
  linkedBuyerCount?: number;
  /** Whether a distributor is linked now (or selected, when creating). */
  hasLinkedDistributor?: boolean;
}

export const UserTypeSection: React.FC<UserTypeSectionProps> = ({
  control,
  errors,
  watch,
  isEditMode = false,
  disabled = false,
  isFederatedUser = false,
  linkedBuyerCount = 0,
  hasLinkedDistributor = false,
}) => {
  const { isSsoUser } = useSsoRestriction();
  // Account type is identity data for SSO users, so it is read-only for them.
  const sectionDisabled = disabled || isSsoUser;
  const watchSupplier = watch('supplier');
  // A user is either a buyer user or a distributor user, so the flag can't be
  // set while buyers are linked, or cleared while a distributor is linked.
  const getSupplierLock = (isDistributor: boolean): string | null => {
    if (!isDistributor && linkedBuyerCount > 0) {
      return 'Remove the linked buyers to make this a distributor user';
    }
    if (isDistributor && hasLinkedDistributor) {
      return 'Remove the linked distributor to make this a buyer user';
    }
    return null;
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center">
          <h3 className="text-lg font-medium text-foreground">User Type</h3>
          <SsoRestrictionTooltip />
        </div>

        {isEditMode && (
          <div className="p-3 bg-default-100 border border-default-200 rounded-lg">
            <p className="text-sm text-default-700">
              <strong>Note:</strong> Test user cannot be changed after creation.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-5">
          <Controller
            name="isTest"
            control={control}
            render={({ field }) => (
              <ChocoCheckbox
                isSelected={field.value}
                onValueChange={field.onChange}
                isDisabled={isEditMode || sectionDisabled}
                size="sm"
                label="Test User"
                description="Mark this user as a test account"
              />
            )}
          />

          <Controller
            name="supplier"
            control={control}
            render={({ field }) => {
              const supplierLock = getSupplierLock(!!field.value);
              return (
                <ChocoCheckbox
                  isSelected={field.value}
                  onValueChange={field.onChange}
                  isDisabled={sectionDisabled || !!supplierLock}
                  size="sm"
                  label="Distributor"
                  description={
                    supplierLock ?? 'This user represents a distributor'
                  }
                />
              );
            }}
          />

          {/* Support Login - only show in edit mode and when user has phone number */}
          {isEditMode && watch('phone') && (
            <Controller
              name="supportLogin"
              control={control}
              render={({ field }) => (
                <SupportLogin
                  isEnabled={field.value || false}
                  onToggle={field.onChange}
                  phoneNumber={watch('phone') || ''}
                  userName={watch('name') || ''}
                  // Support login is an admin capability rather than identity
                  // data, so it is not covered by the SSO read-only rule that
                  // gates the rest of this section.
                  disabled={disabled}
                  isFederatedUser={isFederatedUser}
                />
              )}
            />
          )}
        </div>
      </div>

      {!watchSupplier && (
        <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
          <p className="text-sm text-warning-800">
            <strong>Note:</strong> Users must be either test users,
            distributors, or linked to buyers to be valid.
          </p>
        </div>
      )}

      <Divider />
    </>
  );
};
